// Quick Entry: a new card on top of the list (or in a section, or below a to-do). What the parser reads in the title
// shows as dashed chips while typing; × keeps those words as text. Saved as a to-do on Return, Done, Esc or a click
// beside it; several pasted lines make one to-do each.

import { useCallback, useEffect, useMemo, useRef, useState, type ClipboardEvent, type KeyboardEvent } from "react";
import type { Route } from "../core/lists";
import { sortedHabits } from "../core/habits";
import { parse, tokenID, type QuickToken } from "../core/parser";
import { makeTodo, type ChecklistItem, type Todo } from "../core/records";
import { currentLanguage, t } from "../i18n";
import { Checklist } from "./Checklist";
import { useWindow, type Composing } from "./context";
import { routeTitle, splitEmoji, whenIcon } from "./format";
import { afterFirstFrame } from "./frames";
import { Icon, type IconName } from "./Icon";
import type { QuickEntry } from "./model";
import { applyChange, Properties, usePicker, type PropertyChange } from "./Properties";
import { NotesField, tabToChips, TitleField } from "./TodoCard";

const TOKEN_ICONS: Record<QuickToken["kind"], IconName> = {
  start: "calendar", time: "bellFill", deadline: "due", repeats: "repeat", habit: "flame", tag: "tag", project: "list",
  area: "list",
};

export function QuickEntryCard({ route, composing }: { route: Route; composing: Composing }) {
  const window = useWindow();
  const { model, state, set, showToast } = window;
  const [text, setText] = useState("");
  const [ignoring, setIgnoring] = useState<string[]>([]);
  const [notes, setNotes] = useState("");
  const [checklist, setChecklist] = useState<ChecklistItem[]>([]);
  const [showsChecklist, setShowsChecklist] = useState(false);
  // What was chosen with the tools before saving: a to-do that holds only those values.
  // In a column of the week it starts on that day, as if chosen with When (docs/56).
  const [draft, setDraft] = useState<Todo>(() => makeTodo({ id: "DRAFT", start: composing.day !== undefined ? { kind: "on", day: composing.day } : { kind: "inbox" } }));
  const [chosen, setChosen] = useState<Set<"start" | "deadline" | "tags" | "reminder" | "repeats">>(() => new Set(composing.day !== undefined ? ["start"] : []));
  const picker = usePicker();
  const field = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    field.current?.focus({ preventScroll: true });
    // Into view after the card's first frame, so measuring the page does not delay it (docs/58, idea 3).
    return afterFirstFrame(() => field.current?.closest(".card")?.scrollIntoView({ block: "nearest" }));
  }, []);

  const result = useMemo(() => parse(text, {
    today: state.today,
    tags: [...new Set(state.records.todos.flatMap((todo) => todo.tags))],
    projects: state.records.projects.map((project) => ({ id: project.id, title: project.title, completed: project.completedAt !== undefined })),
    areas: state.records.areas.map((area) => ({ id: area.id, title: area.title })),
    ignoring, language: currentLanguage(),
    // A habit from the words only while one can be made: three free, then Pro (Store.parserContext).
    habits: state.isPro || sortedHabits(state.records.habits).length < 3,
  }), [text, ignoring, state.today, state.records, state.isPro]);

  const entry = useCallback((line: string): QuickEntry => {
    const values: QuickEntry["draft"] = { notes, checklist: checklist.filter((item) => item.title.trim() !== "") };
    if (chosen.has("start")) values.start = draft.start;
    if (chosen.has("deadline")) values.deadline = draft.deadline;
    if (chosen.has("tags")) values.tags = draft.tags;
    if (chosen.has("reminder")) values.reminder = draft.reminder;
    if (chosen.has("repeats")) values.repeats = draft.repeats;
    return { text: line, route, ignoring, sectionID: composing.sectionID, after: composing.after, draft: values };
  }, [notes, checklist, chosen, draft, route, ignoring, composing]);

  const finish = useCallback((created: Todo[]) => {
    set({ composing: null });
    const last = created.at(-1);
    if (!last) return;
    const after = model.getState();
    const shown = composing.day !== undefined && last.start.kind === "on" && last.start.day === composing.day
      || after.lists.sections(route).some((section) => section.todos.some((todo) => todo.id === last.id));
    if (shown) {
      requestAnimationFrame(() => set({ selected: created.map((todo) => todo.id) }));
    } else {
      const list = routeTitle(after.lists.routeFor(last), after.lists);
      showToast(created.length > 1 ? t("{count} to-dos added to {list}", { count: created.length, list }) : t("Added to {list}", { list }), true);
    }
  }, [model, route, set, showToast, composing.day]);

  const save = useCallback(() => {
    const created = model.create(entry(text));
    finish(created ? [created] : []);
  }, [model, entry, text, finish]);

  useEffect(() => {
    const listener = () => save();
    document.addEventListener("todolix:close-card", listener);
    return () => document.removeEventListener("todolix:close-card", listener);
  }, [save]);

  const paste = (event: ClipboardEvent<HTMLTextAreaElement>) => {
    const pasted = event.clipboardData.getData("text/plain");
    const lines = pasted.split(/\r?\n/).map((line) => line.replace(/^\s*(?:[-*•]|\d+[.)]|\[[ x]\])\s+/i, "").trim()).filter(Boolean);
    if (lines.length < 2) return;
    event.preventDefault();
    const typed = text.trim();
    const all = typed ? [typed + " " + lines[0], ...lines.slice(1)] : lines;
    finish(model.createMany(all.map((line) => entry(line))));
  };

  const key = (event: KeyboardEvent) => {
    if (event.defaultPrevented) return;
    // A picker's keys are its own (it is drawn outside the card).
    if (!event.currentTarget.contains(event.target as Node)) return;
    if (event.key === "Escape") { event.preventDefault(); event.stopPropagation(); save(); }
    if (event.key === "Enter" && (event.metaKey || event.ctrlKey)) { event.preventDefault(); save(); }
  };

  const change = (property: PropertyChange) => {
    const next = applyChange(draft, property, state.today, state.zone);
    const marks = new Set(chosen);
    if (property.start) marks.add("start");
    if (property.deadline !== undefined) marks.add("deadline");
    if (property.toggleTag) marks.add("tags");
    if (property.reminder !== undefined) { marks.add("reminder"); marks.add("start"); }
    if (property.repeats !== undefined) { marks.add("repeats"); marks.add("start"); }
    setChosen(marks);
    setDraft(next);
  };

  // Where it will go: a project or area read from the title, else the list's own place.
  const placeToken = result.tokens.find((token) => token.kind === "project" || token.kind === "area");
  let where: string;
  if (placeToken) where = placeToken.label;
  else if (route.kind === "project" || route.kind === "area") where = routeTitle(route, state.lists);
  else if (["today", "scheduled", "anytime", "someday", "general", "due"].includes(route.kind)) where = t("Miscellaneous");
  else where = t("Inbox");
  if (route.kind === "area" && !placeToken) where = [splitEmoji(where).emoji, splitEmoji(where).name].filter(Boolean).join(" ");

  const tokenChips = result.tokens.map((token) => (
    <span key={tokenID(token)} className="chip parsed">
      <Icon name={token.kind === "start" ? whenIcon(token.value, state.today) : TOKEN_ICONS[token.kind]} />
      {token.label}
      <button className="x" aria-label={t("Keep “{words}” as Text", { words: token.text })} title={t("Keep as Text")}
        onClick={() => { setIgnoring((current) => [...current, tokenID(token)]); field.current?.focus(); }}>
        <Icon name="x" />
      </button>
    </span>
  ));

  // The chips of chosen values show beside the parsed ones; those the parser also read give way to the parser's.
  const shownDraft: Todo = { ...draft, checklist };
  if (!chosen.has("start")) shownDraft.start = { kind: "inbox" };

  return (
    <div className="row card quick-entry" data-card="new" onKeyDown={key} role="group" aria-label={t("New To-Do")}>
      <div className="card-top">
        <span className="check" aria-hidden="true" />
        <TitleField ref={field} value={text} placeholder={t("New To-Do")} aria-label={t("New To-Do")}
          onText={setText} onPaste={paste}
          onKeyDown={(event) => {
            if (event.key === "Enter" && !event.nativeEvent.isComposing && !event.metaKey && !event.ctrlKey) { event.preventDefault(); save(); }
            if (event.key === "Tab" && !event.shiftKey) tabToChips(event);
          }} />
        <span />
      </div>
      <div className="card-inner">
        <div className="card-where">{where}</div>
        {showsChecklist && <Checklist items={checklist} onChange={setChecklist} focusNew />}
        <Properties todo={shownDraft} picker={picker} onChange={change} before={tokenChips}
          tools={["when", "deadline", "tags", "checklist"]} onChecklist={() => setShowsChecklist(true)} />
        <NotesField value={notes} placeholder={t("Notes")} aria-label={t("Notes")} onText={setNotes} />
      </div>
    </div>
  );
}
